/**
 * Les formulaires exigés par le dossier, dans l'étape du cahier de charges.
 *
 * ## Pourquoi ici, et pas dans un écran à part
 *
 * Le cahier de charges est déjà la surface où le conseiller prépare ce que
 * l'assureur recevra. Créer une seconde étape « Formulaires » à côté aurait
 * dédoublé le parcours pour la même intention — et obligé à choisir laquelle
 * regarder.
 *
 * ## Ce que ce bloc N'IMPLÉMENTE PAS
 *
 * Rien de la plomberie : une exigence de formulaire est une ligne
 * `project_requirements(kind='form_response')` dont la clé est une **version
 * publiée**, satisfaite par une réponse soumise de cette version exacte, et la
 * readiness d'envoi lit déjà ce verdict. Ce bloc **affiche** cet état et offre
 * les deux gestes ; il ne redéfinit aucune règle.
 *
 * ## Aucun formulaire particulier n'est connu de ce code
 *
 * Un modèle publié devient exigible comme n'importe quel autre. Rien n'est codé
 * en dur — ni GFS, ni aucun autre.
 */

import {
  Schemas,
  publicUnfillableRequiredFields,
  type RequirementState,
} from "@kollectivia/shared";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { useFormVersion } from "../../../lib/queries/formTemplates";
import { useProjectCompleteness } from "../../../lib/queries/projectCompleteness";
import { useProject } from "../../../lib/queries/projects";
import { construireDetour } from "../../../lib/retourParcours";
import { AskClientDialog } from "../../projects/_parts/AskClientDialog";

type Props = {
  readonly projectId: string | undefined;
  /** La demande en préparation — elle porte le retour du détour de remplissage. */
  readonly demandeId: string;
};

/**
 * Une exigence, avec le chemin de remplissage.
 *
 * La clé de l'exigence est une **version**, mais l'écran de remplissage est
 * adressé par `modèle + version` : on lit donc la version pour retrouver son
 * modèle. Un composant par ligne, plutôt qu'une boucle de hooks — c'est la
 * seule façon correcte d'appeler un hook par élément.
 */
function LigneFormulaireRequis({
  etat,
  demandeId,
  onDemander,
}: {
  etat: RequirementState;
  demandeId: string;
  onDemander: () => void;
}) {
  const { t, i18n } = useTranslation();
  const r = etat.requirement;
  const version = useFormVersion(r.requirement_key);
  const reserveAuCabinet =
    version.data !== undefined && publicUnfillableRequiredFields(version.data.schema).length > 0;
  const libelle = i18n.language.startsWith("en") ? r.label_en : r.label_fr;

  if (
    Schemas.formPurpose(version.data?.schema) === "mandate" ||
    Schemas.hasSignatureFields(version.data?.schema)
  )
    return null;

  const cheminRemplir =
    version.data?.template_id !== undefined &&
    Schemas.formPurpose(version.data.schema) !== "mandate" &&
    !Schemas.hasSignatureFields(version.data.schema)
      ? `/formulaires/${version.data.template_id}/versions/${r.requirement_key}/remplir`
      : null;

  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-slate-100 py-3 first:border-t-0">
      <span
        aria-hidden="true"
        className={`h-2 w-2 shrink-0 rounded-full ${
          etat.satisfied ? "bg-emerald-500" : etat.overdue ? "bg-red-500" : "bg-amber-500"
        }`}
      />
      <span className="min-w-0 flex-1">
        <span
          className={`block truncate text-sm ${
            etat.satisfied ? "text-slate-500 line-through" : "font-medium text-slate-900"
          }`}
        >
          {libelle}
        </span>
        <span className="mt-0.5 block text-xs text-slate-600">
          {version.data?.version_number !== undefined
            ? t("completeness.dialog.form_version_label", { n: version.data.version_number })
            : t("app.loading")}
          {" · "}
          {t(`completeness.waiting_on.${r.audience}`)}
          {r.blocks_insurer_send && (
            <>
              {" · "}
              <span className="font-medium text-amber-700">
                {t("prepare_send.required_forms.blocking")}
              </span>
            </>
          )}
        </span>
      </span>

      <span
        className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${
          etat.satisfied ? "bg-emerald-50 text-emerald-800" : "bg-amber-50 text-amber-800"
        }`}
      >
        {etat.satisfied
          ? t("prepare_send.required_forms.done")
          : t("prepare_send.required_forms.todo")}
      </span>

      {/* Les gestes ne servent plus à rien une fois l'exigence satisfaite. */}
      {!etat.satisfied && (
        <span className="flex shrink-0 flex-wrap gap-2">
          {cheminRemplir !== null && (
            <Link
              to={construireDetour({
                vers: cheminRemplir,
                // RETOUR SEUL. `demande-preparer` ferait de cette réponse
                // le cahier de charges de la demande — donc ce que l'assureur
                // reçoit. Un formulaire simplement exigé n'a rien à y faire.
                destination: "demande-preparer-retour",
                retourId: demandeId,
              })}
              className="focus-visible:ring-brand-500 touch-manipulation rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
            >
              {t("prepare_send.required_forms.fill_now")}
            </Link>
          )}
          {/* Envoyer n'a de sens que si c'est le client qu'on attend. */}
          {r.audience === "client" && version.data !== undefined && !reserveAuCabinet && (
            <button
              type="button"
              onClick={onDemander}
              className="focus-visible:ring-brand-500 touch-manipulation rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
            >
              {t("prepare_send.required_forms.send_to_client")}
            </button>
          )}
          {r.audience === "client" && reserveAuCabinet && (
            <span className="text-sm text-amber-800">
              {t("completeness.dialog.form_broker_only")}
            </span>
          )}
        </span>
      )}
    </li>
  );
}

export function FormulairesRequis({ projectId, demandeId }: Props) {
  const { t, i18n } = useTranslation();
  const completude = useProjectCompleteness(projectId);
  // L'entreprise vient du DOSSIER, pas de la demande : c'est elle qui porte les
  // contacts à qui l'on peut envoyer un formulaire.
  const dossier = useProject(projectId);
  const [aDemander, setADemander] = useState<RequirementState | null>(null);

  const requis = (completude.data?.requirements ?? []).filter(
    (e) => e.requirement.kind === "form_response",
  );

  // Aucun formulaire exigé : on ne rend rien. Un bloc vide dirait « il manque
  // quelque chose ici » alors que c'est le cas le plus courant.
  if (requis.length === 0) return null;

  const enAttente = requis.filter((e) => !e.satisfied).length;

  return (
    <section className="mt-5 rounded-lg border border-slate-200 bg-white p-4">
      <h4 className="text-sm font-semibold text-slate-900">
        {t("prepare_send.required_forms.title")}
      </h4>
      <p className="mt-0.5 text-xs text-slate-600" aria-live="polite">
        {enAttente === 0
          ? t("prepare_send.required_forms.all_done")
          : t("prepare_send.required_forms.remaining", { count: enAttente })}
      </p>

      <ul className="mt-2">
        {requis.map((e) => (
          <LigneFormulaireRequis
            key={e.requirement.id}
            etat={e}
            demandeId={demandeId}
            onDemander={() => setADemander(e)}
          />
        ))}
      </ul>

      {aDemander !== null && projectId !== undefined && (
        <AskClientDialog
          projectId={projectId}
          companyId={dossier.data?.company_id ?? null}
          requirement={{
            id: aDemander.requirement.id,
            kind: aDemander.requirement.kind,
            requirement_key: aDemander.requirement.requirement_key,
            label: i18n.language.startsWith("en")
              ? aDemander.requirement.label_en
              : aDemander.requirement.label_fr,
          }}
          missingInformationId={aDemander.missing_information_id}
          onClose={() => setADemander(null)}
        />
      )}
    </section>
  );
}
